import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useRooms } from '@/hooks/useRooms';
import { useClients } from '@/hooks/useClients';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { BedDouble, Plus, Trash2, Users } from 'lucide-react';

type RoomType = 'single' | 'shared' | 'communal';

interface FacilityRoomsTabProps {
  facilityId: string;
  isDemo?: boolean;
}

const roomTypeConfig: Record<RoomType, { label: string; description: string; color: string }> = {
  single: { label: 'Single', description: '1 bed', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  shared: { label: 'Shared', description: '2 beds (a/b)', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' },
  communal: { label: 'Communal', description: 'Multiple beds', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
};

export function FacilityRoomsTab({ facilityId, isDemo = false }: FacilityRoomsTabProps) {
  const { rooms } = useRooms();
  const { clients } = useClients();
  const queryClient = useQueryClient();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newRoomNumber, setNewRoomNumber] = useState('');
  const [newRoomType, setNewRoomType] = useState<RoomType>('single');
  const [newRoomCapacity, setNewRoomCapacity] = useState('4');

  const facilityRooms = rooms
    .filter((r: any) => r.site_id === facilityId)
    .sort((a: any, b: any) => {
      const numA = parseInt(a.room_number) || 0;
      const numB = parseInt(b.room_number) || 0;
      if (numA !== numB) return numA - numB;
      return a.room_number.localeCompare(b.room_number);
    });

  const getRoomOccupants = (roomId: string) => {
    return (clients as any[]).filter(c => c.room_id === roomId && c.status === 'checked-in');
  };

  const handleAddRoom = async () => {
    if (!newRoomNumber) {
      toast.error('Please enter a room number');
      return;
    }

    try {
      if (newRoomType === 'shared') {
        const roomsToInsert = [
          { site_id: facilityId, room_number: `${newRoomNumber}a`, capacity: 1, current_occupancy: 0, is_occupied: false, room_type: 'shared' },
          { site_id: facilityId, room_number: `${newRoomNumber}b`, capacity: 1, current_occupancy: 0, is_occupied: false, room_type: 'shared' },
        ];
        const { error } = await supabase.from('rooms').insert(roomsToInsert);
        if (error) throw error;
        toast.success(`Shared rooms ${newRoomNumber}a and ${newRoomNumber}b created`);
      } else if (newRoomType === 'communal') {
        const capacity = parseInt(newRoomCapacity) || 4;
        const { error } = await supabase.from('rooms').insert({
          site_id: facilityId,
          room_number: newRoomNumber,
          capacity,
          current_occupancy: 0,
          is_occupied: false,
          room_type: 'communal',
        });
        if (error) throw error;
        toast.success(`Communal room ${newRoomNumber} created (${capacity} beds)`);
      } else {
        const { error } = await supabase.from('rooms').insert({
          site_id: facilityId,
          room_number: newRoomNumber,
          capacity: 1,
          current_occupancy: 0,
          is_occupied: false,
          room_type: 'single',
        });
        if (error) throw error;
        toast.success(`Single room ${newRoomNumber} created`);
      }

      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      setIsAddOpen(false);
      setNewRoomNumber('');
      setNewRoomType('single');
      setNewRoomCapacity('4');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create room');
    }
  };

  const handleDeleteRoom = async (roomId: string, roomNumber: string) => {
    const occupants = getRoomOccupants(roomId);
    if (occupants.length > 0) {
      toast.error('Cannot delete an occupied room');
      return;
    }

    if (!confirm(`Delete room ${roomNumber}?`)) return;

    try {
      const { error } = await supabase.from('rooms').delete().eq('id', roomId);
      if (error) throw error;
      toast.success(`Room ${roomNumber} deleted`);
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete room');
    }
  };

  const getRoomTypeDisplay = (room: any): RoomType => {
    return room.room_type || 'single';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Rooms</h3>
          <p className="text-sm text-muted-foreground">{facilityRooms.length} rooms configured</p>
        </div>
        {!isDemo && (
          <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Room
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Room</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Room Type</Label>
                  <Select value={newRoomType} onValueChange={(v) => setNewRoomType(v as RoomType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">
                        <span className="flex items-center gap-2">Single — 1 bed per room</span>
                      </SelectItem>
                      <SelectItem value="shared">
                        <span className="flex items-center gap-2">Shared — 2 beds (creates a/b)</span>
                      </SelectItem>
                      <SelectItem value="communal">
                        <span className="flex items-center gap-2">Communal — multiple beds</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Room Number</Label>
                  <Input
                    value={newRoomNumber}
                    onChange={(e) => setNewRoomNumber(e.target.value)}
                    placeholder="e.g. 16"
                  />
                  {newRoomType === 'shared' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Creates {newRoomNumber || '?'}a and {newRoomNumber || '?'}b
                    </p>
                  )}
                </div>
                {newRoomType === 'communal' && (
                  <div>
                    <Label>Beds in Room</Label>
                    <Input
                      type="number"
                      value={newRoomCapacity}
                      onChange={(e) => setNewRoomCapacity(e.target.value)}
                      placeholder="4"
                      min="2"
                    />
                  </div>
                )}
                <Button onClick={handleAddRoom} className="w-full">
                  Create Room{newRoomType === 'shared' ? 's' : ''}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {facilityRooms.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BedDouble className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No rooms configured for this facility</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {facilityRooms.map((room: any) => {
            const occupants = getRoomOccupants(room.id);
            const isOccupied = occupants.length > 0;
            const roomType = getRoomTypeDisplay(room);
            const typeInfo = roomTypeConfig[roomType];

            return (
              <Card
                key={room.id}
                className={`relative group transition-all ${
                  isOccupied
                    ? 'border-primary/30 bg-primary/5'
                    : 'border-border'
                }`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-lg">{room.room_number}</span>
                    <Badge variant={isOccupied ? 'default' : 'outline'} className="text-xs">
                      {isOccupied ? 'Occupied' : 'Open'}
                    </Badge>
                  </div>
                  <div className="mb-2">
                    <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${typeInfo.color}`}>
                      {typeInfo.label} · {roomType === 'communal' ? `${room.capacity} beds` : typeInfo.description}
                    </span>
                  </div>
                  {occupants.length > 0 && (
                    <div className="space-y-1">
                      {occupants.map((c: any) => (
                        <p key={c.id} className="text-xs text-muted-foreground truncate">
                          {c.first_name} {c.last_name}
                        </p>
                      ))}
                    </div>
                  )}
                  {!isDemo && !isOccupied && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-destructive"
                      onClick={() => handleDeleteRoom(room.id, room.room_number)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
